import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, Stack, Typography,
} from '@mui/material';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { backendRoot } from '../config';

// Pole (terminal) and home markers are required on the drawing canvas so the
// user always has both endpoints in view while tracing the route.
const POLE_ICON = L.divIcon({
  className: '',
  iconSize: [26, 34],
  iconAnchor: [13, 32],
  html:
    "<svg xmlns='http://www.w3.org/2000/svg' width='26' height='34' viewBox='0 0 26 34'>" +
    "<line x1='13' y1='4' x2='13' y2='32' stroke='#3E2723' stroke-width='3'/>" +
    "<line x1='4' y1='9' x2='22' y2='9' stroke='#3E2723' stroke-width='2.5'/>" +
    "<line x1='6' y1='14' x2='20' y2='14' stroke='#3E2723' stroke-width='2'/>" +
    "<circle cx='13' cy='4' r='3' fill='#FFD600' stroke='#000' stroke-width='1.5'/></svg>",
});

const HOME_ICON = L.divIcon({
  className: '',
  iconSize: [30, 40],
  iconAnchor: [15, 38],
  html:
    "<svg xmlns='http://www.w3.org/2000/svg' width='30' height='40' viewBox='0 0 30 40'>" +
    "<path d='M15 2 L28 14 H24 V32 H6 V14 H2 Z' fill='#EA4335' stroke='#7B231E' stroke-width='1.5'/>" +
    "<rect x='12' y='21' width='6' height='11' fill='#7B231E'/></svg>",
});

const VERTEX_ICON = L.divIcon({
  className: '',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
  html:
    "<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12'>" +
    "<circle cx='6' cy='6' r='4.5' fill='#FFFFFF' stroke='#00BCD4' stroke-width='2.5'/></svg>",
});

const round6 = (n) => Math.round(n * 1e6) / 1e6;

export default function DesignEditPane({ open, rowId, onClose, onSaved }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const drawLayerRef = useRef(null);

  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [points, setPoints] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Leaflet mutates outside React; keep the latest points readable from the
  // click handler without re-binding the map on every vertex.
  const pointsRef = useRef(points);
  pointsRef.current = points;

  // --- load pole/home coordinates ---------------------------------------
  useEffect(() => {
    if (!open || !rowId) return undefined;
    let cancelled = false;
    setLoadingContext(true);
    setError(null);
    setResult(null);
    setPoints([]);

    fetch(`${backendRoot}/api/design-input/${rowId}/edit-design/`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.status !== 'success') throw new Error(data.message || 'Could not load record');
        setContext(data);
        // The drop always originates at the terminal, so seed that vertex.
        setPoints([[data.terminal.lat, data.terminal.lng]]);
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoadingContext(false));

    return () => { cancelled = true; };
  }, [open, rowId]);

  // --- build the map once context is available --------------------------
  useEffect(() => {
    if (!open || !context || !containerRef.current) return undefined;

    const map = L.map(containerRef.current, { doubleClickZoom: false });
    mapRef.current = map;

    // Must match the design renderer's tile source (Google Maps Tile API with a
    // session token) or the user traces on different-vintage imagery than the
    // design image shows. Falls back to the unauthenticated endpoint only if the
    // backend could not mint a session.
    if (context.tile_url) {
      L.tileLayer(context.tile_url, { maxZoom: 21, attribution: 'Google Map Services' }).addTo(map);
    } else {
      L.tileLayer('http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
        maxZoom: 21, subdomains: ['mt0', 'mt1', 'mt2', 'mt3'], attribution: 'Google Satellite',
      }).addTo(map);
    }

    // The design that already exists, shown dashed so the user can trace or
    // deviate from it rather than starting blind.
    const existing = Array.isArray(context.existing_path) ? context.existing_path : null;
    if (existing && existing.length >= 2) {
      L.polyline(existing, {
        color: '#FFFFFF', weight: 3, opacity: 0.85, dashArray: '6,6',
      }).addTo(map).bindTooltip('Current design');
    }

    L.marker([context.terminal.lat, context.terminal.lng], { icon: POLE_ICON })
      .addTo(map)
      .bindTooltip(`Terminal ${context.terminal.terminal_id || ''}`);
    L.marker([context.address_point.lat, context.address_point.lng], { icon: HOME_ICON })
      .addTo(map)
      .bindTooltip(context.address || 'Address');

    drawLayerRef.current = L.layerGroup().addTo(map);

    // Frame the whole existing route, matching the design image's own
    // fit_bounds(path_coords). Framing just the two endpoints zooms far tighter
    // than the design image, which reads as a different location.
    const frame = existing && existing.length >= 2
      ? existing
      : [
        [context.terminal.lat, context.terminal.lng],
        [context.address_point.lat, context.address_point.lng],
      ];
    map.fitBounds(frame, { padding: [70, 70], maxZoom: 20 });

    map.on('click', (e) => {
      setPoints([...pointsRef.current, [round6(e.latlng.lat), round6(e.latlng.lng)]]);
      setResult(null);
    });

    return () => {
      map.remove();
      mapRef.current = null;
      drawLayerRef.current = null;
    };
  }, [open, context]);

  // --- redraw the working polyline on every change ----------------------
  useEffect(() => {
    const layer = drawLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (points.length >= 2) {
      L.polyline(points, { color: '#00FFFF', weight: 4 }).addTo(layer);
    }
    points.forEach((p, i) => {
      L.marker(p, { icon: VERTEX_ICON })
        .addTo(layer)
        .bindTooltip(i === 0 ? 'Start (terminal)' : `Point ${i + 1}`);
    });
  }, [points]);

  const post = useCallback(async (save) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${backendRoot}/api/design-input/${rowId}/edit-design/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: points, save }),
      });
      const data = await res.json();
      if (!res.ok || data.status !== 'success') {
        throw new Error(data.message || `Request failed (${res.status})`);
      }
      setResult(data);
      if (save && data.saved) onSaved?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [points, rowId, onSaved]);

  const canSubmit = points.length >= 2 && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Edit Design
        <Typography variant="body2" color="text.secondary">
          {context?.address || ''}
          {context?.current_version ? ` — current version v${context.current_version}` : ''}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {loadingContext && <Stack alignItems="center" py={4}><CircularProgress /></Stack>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {context && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              The dashed white line is the current design. Click on the map to trace a new drop path
              from the pole to the home &mdash; the first point is placed at the terminal for you.
              Flowerpots, driveway bores and road bores are detected automatically when you recompute.
            </Alert>

            <Box sx={{ height: 460, width: '100%', borderRadius: 1, overflow: 'hidden', border: '1px solid #ddd' }}>
              <div ref={containerRef} style={{ height: '100%', width: '100%' }} />
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
              <Chip label={`${points.length} point${points.length === 1 ? '' : 's'}`} size="small" />
              <Button size="small" disabled={points.length <= 1 || busy}
                onClick={() => { setPoints(points.slice(0, -1)); setResult(null); }}>
                Undo point
              </Button>
              <Button size="small" disabled={points.length <= 1 || busy}
                onClick={() => { setPoints(points.slice(0, 1)); setResult(null); }}>
                Clear
              </Button>
              <Button size="small" disabled={busy || !context}
                onClick={() => {
                  setPoints([...pointsRef.current, [context.address_point.lat, context.address_point.lng]]);
                  setResult(null);
                }}>
                Snap to home
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" disabled={!canSubmit} onClick={() => post(false)}>
                {busy ? 'Analysing…' : 'Recompute design'}
              </Button>
            </Stack>

            {result && (
              <>
                <Divider sx={{ my: 2 }} />
                {result.warning && <Alert severity="warning" sx={{ mb: 2 }}>{result.warning}</Alert>}
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  <Chip color="primary" label={`Route: ${result.route_length_ft} ft`} />
                  <Chip label={`Flowerpots: ${result.flowerpots}`} />
                  <Chip label={`Driveway bores: ${result.driveway_bores} (${result.driveway_bore_ft} ft)`} />
                  <Chip label={`Road bores: ${result.road_bores} (${result.road_bore_ft} ft)`} />
                  {result.saved && <Chip color="success" label={`Saved as v${result.version_id}`} />}
                </Stack>

                {/*
                  The image arrives as a base64 data URI. It is deliberately NOT loaded
                  from a URL: /static/data/... 404s under gunicorn in production (no
                  WhiteNoise, and the file is written after collectstatic), and the GCS
                  url 403s because the uploader never makes the blob public. A data URI
                  renders identically in every environment.
                */}
                {result.image_data_uri && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Recomputed design</Typography>
                    <img src={result.image_data_uri} alt="Recomputed design"
                      style={{ maxWidth: '100%', border: '1px solid #ddd', borderRadius: 4 }} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="contained" disabled={!result || busy || result.saved}
          onClick={() => post(true)}>
          {busy ? 'Saving…' : 'Save as new version'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
